import { useState, useRef, useEffect, type ChangeEvent } from 'react'
import type { CSSProperties, RefObject } from 'react'
import useAuthStore from '../../store/authStore.js'
import { useSettingsStore } from '../../store/settingsStore.js'
import { useLanguage } from '../../i18n/index.jsx'
import api from '../../utils/api.js'
import WelcomeStep from './WelcomeStep.jsx'
import CompanyStep from './CompanyStep.jsx'
import TemplateStep from './TemplateStep.jsx'
import DepartmentStep from './DepartmentStep.jsx'
import VenueStep from './VenueStep.jsx'
import DoneStep from './DoneStep.jsx'
import CustomFieldsRenderer, { validateCustomFields, type CustomField, type FieldAnswers } from './CustomFieldsRenderer.jsx'
import { logError } from '../../utils/logError.js'
import './OnboardingWizard.css'

interface OnboardingWizardProps {
  onComplete: () => void
}

interface OnboardingStepConfig {
  id: string
  enabled: boolean
  order: number
  custom_fields?: CustomField[]
  title?: string
  subtitle?: string
  [key: string]: unknown
}

interface TemplateResult {
  departments: number
  categories: number
  locations: number
}

interface OnboardingTemplate {
  id: string
  name: string
  description: string
  departments: { name: string; color: string }[]
  /** Wave-5: tenant org_types this template is preselected for. */
  recommended_for?: string[]
}

interface ProgressDotsProps {
  current: string
  steps: string[]
}

const DEFAULT_STEPS = ['welcome', 'company', 'template', 'department', 'venue', 'done']

const DEPT_COLORS = [
  '#6366f1', '#8b5cf6', '#ec4899', '#ef4444',
  '#f97316', '#eab308', '#22c55e', '#14b8a6',
  '#3b82f6', '#06b6d4',
]

function ProgressDots({ current, steps }: ProgressDotsProps) {
  return (
    <div style={{ display: 'flex', gap: 8, justifyContent: 'center', marginBottom: 40 }}>
      {steps.slice(1).map((s, i) => {
        const stepIdx = i + 1
        const currentIdx = steps.indexOf(current)
        const done    = stepIdx < currentIdx
        const active  = stepIdx === currentIdx
        return (
          <div key={s} style={{
            width: active ? 24 : 8, height: 8, borderRadius: 4,
            background: done || active ? 'var(--accent)' : 'var(--border-default)',
            transition: 'all 0.3s ease',
          }} />
        )
      })}
    </div>
  )
}

export default function OnboardingWizard({ onComplete }: OnboardingWizardProps) {
  const { t, lang } = useLanguage()
  const { setCompanyName, setLogoUrl } = useSettingsStore()
  // Wave-5: the tenant's vertical (set at signup) preselects the matching
  // template set. Exposed by /auth/me + login as tenant.org_type.
  const orgType = useAuthStore(s => s.tenant?.org_type) || 'theater'
  const [activeSteps, setActiveSteps] = useState<string[]>(DEFAULT_STEPS)
  const [stepConfig, setStepConfig]   = useState<Record<string, OnboardingStepConfig>>({})
  const [_configLoaded, setConfigLoaded] = useState(false)
  const [step, setStep]               = useState('welcome')
  const [companyName, setName]        = useState('')
  const [logoPreview, setLogoPreview] = useState<string | null>(null)
  const [logoUploading, setLogoUploading] = useState(false)
  const [logoError, setLogoError]     = useState('')
  const [deptName, setDeptName]       = useState('')
  const [deptColor, setDeptColor]     = useState(DEPT_COLORS[0])
  const [venueName, setVenueName]     = useState('')
  const [loading, setLoading]         = useState(false)
  const [error, setError]             = useState('')
  const fileRef = useRef<HTMLInputElement>(null) as RefObject<HTMLInputElement>
  // §4.8 fix: track the "template applied" auto-advance timer so it doesn't
  // re-advance the wizard after the user navigates away. The old bare
  // setTimeout could call goNext() after unmount, re-writing wizardStep.
  const autoNextTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  useEffect(() => () => {
    if (autoNextTimerRef.current) clearTimeout(autoNextTimerRef.current)
  }, [])

  // Custom field answers
  const [answers, setAnswers] = useState<FieldAnswers>({})

  // Template state
  const [templates, setTemplates]           = useState<OnboardingTemplate[]>([])
  const [selectedTemplate, setSelectedTemplate] = useState<string | null>(null)
  const [templateApplied, setTemplateApplied]   = useState(false)
  const [templateResult, setTemplateResult]     = useState<TemplateResult | null>(null)

  // Fetch onboarding config (platform default or tenant override)
  useEffect(() => {
    api.get<{ steps?: OnboardingStepConfig[] }>('/settings/onboarding-config')
      .then(res => {
        const steps = res.data?.steps || []
        if (steps.length) {
          const enabled = steps
            .filter(s => s.enabled)
            .sort((a, b) => a.order - b.order)
            .map(s => s.id)
          if (!enabled.includes('welcome')) enabled.unshift('welcome')
          if (!enabled.includes('done')) enabled.push('done')
          setActiveSteps(enabled)
          const cfg: Record<string, OnboardingStepConfig> = {}
          steps.forEach(s => { cfg[s.id] = s })
          setStepConfig(cfg)
        }
      })
      .catch(err => { logError('OnboardingWizard.loadConfig', err) })
      .finally(() => setConfigLoaded(true))
  }, [])

  const getStepCustomFields = (stepId: string): CustomField[] => stepConfig[stepId]?.custom_fields || []

  const goNext = () => {
    // Validate required custom fields before advancing
    const fields = getStepCustomFields(step)
    if (fields.length && !validateCustomFields(fields, answers)) {
      setError(t('fillRequiredFields'))
      return
    }
    setError('')
    setStep(s => activeSteps[activeSteps.indexOf(s) + 1])
  }

  // Fetch templates when entering the template step (or language changes)
  useEffect(() => {
    if (step === 'template') {
      api.get<OnboardingTemplate[]>(`/settings/onboarding-templates?lang=${lang}`)
        .then(res => {
          setTemplates(res.data)
          // Wave-5: preselect the template recommended for this tenant's
          // vertical — but never override a choice the user already made.
          setSelectedTemplate(prev => {
            if (prev) return prev
            const match = res.data.find(tmpl => tmpl.recommended_for?.includes(orgType))
            return match ? match.id : null
          })
        })
        .catch(() => { /* templates are optional, skip on error */ })
    }
  }, [step, lang, orgType])

  // ── Step handlers ──────────────────────────────────────────

  const handleLogoChange = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    const reader = new FileReader()
    reader.onload = ev => setLogoPreview(ev.target?.result as string ?? null)
    reader.readAsDataURL(file)
    setLogoUploading(true)
    setLogoError('')
    try {
      const fd = new FormData()
      fd.append('file', file)
      const res = await api.post<{ logo_url: string }>('/settings/logo', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      setLogoUrl(res.data.logo_url)
    } catch (err) {
      setLogoError(t('logoUploadFailed'))
    } finally {
      setLogoUploading(false)
    }
  }

  const saveCompany = async () => {
    if (!companyName.trim()) { setError(t('companyNameRequired')); return }
    setError(''); setLoading(true)
    try {
      await api.post('/settings/company-name', { company_name: companyName.trim() })
      setCompanyName(companyName.trim())
      goNext()
    } catch (err) {
      const e = err as { response?: { data?: { error?: string } }; message?: string }
      const msg = e.response?.data?.error || e.message || t('saveFailed')
      setError(`${t('failedToSaveCompanyName')}: ${msg}`)
    } finally {
      setLoading(false)
    }
  }

  const applyTemplate = async () => {
    if (!selectedTemplate) { goNext(); return }
    setError(''); setLoading(true)
    try {
      const res = await api.post<TemplateResult>('/settings/apply-template', { template_id: selectedTemplate, lang })
      setTemplateResult(res.data)
      setTemplateApplied(true)
      if (autoNextTimerRef.current) clearTimeout(autoNextTimerRef.current)
      autoNextTimerRef.current = setTimeout(() => goNext(), 800)
    } catch (err) {
      const e = err as { response?: { data?: { error?: string } }; message?: string }
      const msg = e.response?.data?.error || e.message || t('saveFailed')
      setError(msg)
    } finally {
      setLoading(false)
    }
  }

  const saveDept = async () => {
    if (!deptName.trim()) { setError(t('departmentNameRequired')); return }
    setError(''); setLoading(true)
    try {
      await api.post('/departments', { name: deptName.trim(), color: deptColor })
      goNext()
    } catch { setError(t('createDepartmentFailed')) }
    finally { setLoading(false) }
  }

  const saveVenue = async () => {
    if (!venueName.trim()) { setError(t('venueNameRequired')); return }
    setError(''); setLoading(true)
    try {
      await api.post('/locations', { name: venueName.trim(), type: 'site' })
      setVenueName('')
      goNext()
    } catch { setError(t('createVenueFailed')) }
    finally { setLoading(false) }
  }

  const finish = async () => {
    setLoading(true)
    try {
      // Save custom field answers if any were collected
      if (Object.keys(answers).length > 0) {
        await api.post('/settings/onboarding-answers', { answers }).catch(err => logError('onboarding.saveAnswers', err))
      }
      await api.post('/settings/onboarding-complete')
      onComplete()
    } catch { onComplete() }
    finally { setLoading(false) }
  }

  // ── Shared card style ──────────────────────────────────────

  const card: CSSProperties = {
    background: 'var(--bg-surface)',
    borderRadius: 16,
    padding: '48px 52px',
    maxWidth: 520,
    width: '100%',
    boxShadow: '0 24px 64px rgba(0,0,0,0.3)',
    animation: 'wizardFadeIn 0.35s ease',
  }

  // ── Render ─────────────────────────────────────────────────

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 'var(--z-fullscreen, 3000)',
      background: 'var(--bg-base)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: '1rem',
    }}>
      <div style={card}>
        {step !== 'welcome' && step !== 'done' && <ProgressDots current={step} steps={activeSteps} />}

        {step === 'welcome' && activeSteps.includes('welcome') && (
          <>
            <WelcomeStep stepConfig={stepConfig} goNext={goNext} t={t} />
            <CustomFieldsRenderer fields={getStepCustomFields('welcome')} answers={answers} onChange={setAnswers} t={t} />
          </>
        )}

        {step === 'company' && activeSteps.includes('company') && (
          <>
            <CompanyStep
              stepConfig={stepConfig} companyName={companyName} setName={setName}
              logoPreview={logoPreview} logoUploading={logoUploading} logoError={logoError}
              fileRef={fileRef} handleLogoChange={handleLogoChange}
              saveCompany={saveCompany} error={error} loading={loading} t={t}
            />
            <CustomFieldsRenderer fields={getStepCustomFields('company')} answers={answers} onChange={setAnswers} t={t} />
          </>
        )}

        {step === 'template' && activeSteps.includes('template') && (
          <>
            <TemplateStep
              stepConfig={stepConfig} templates={templates}
              selectedTemplate={selectedTemplate} setSelectedTemplate={setSelectedTemplate}
              templateApplied={templateApplied} templateResult={templateResult}
              applyTemplate={applyTemplate}
              error={error} setError={setError} loading={loading} goNext={goNext} t={t}
            />
            <CustomFieldsRenderer fields={getStepCustomFields('template')} answers={answers} onChange={setAnswers} t={t} />
          </>
        )}

        {step === 'department' && activeSteps.includes('department') && (
          <>
            <DepartmentStep
              templateApplied={templateApplied}
              deptName={deptName} setDeptName={setDeptName}
              deptColor={deptColor} setDeptColor={setDeptColor}
              saveDept={saveDept} error={error} setError={setError}
              loading={loading} goNext={goNext} t={t}
            />
            <CustomFieldsRenderer fields={getStepCustomFields('department')} answers={answers} onChange={setAnswers} t={t} />
          </>
        )}

        {step === 'venue' && activeSteps.includes('venue') && (
          <>
            <VenueStep
              venueName={venueName} setVenueName={setVenueName}
              saveVenue={saveVenue} error={error} setError={setError}
              loading={loading} goNext={goNext} t={t}
            />
            <CustomFieldsRenderer fields={getStepCustomFields('venue')} answers={answers} onChange={setAnswers} t={t} />
          </>
        )}

        {step === 'done' && activeSteps.includes('done') && (
          <DoneStep finish={finish} loading={loading} t={t} />
        )}
      </div>
    </div>
  )
}
